import { CommonModule, TitleCasePipe } from "@angular/common";
import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import saveAs from 'file-saver';
import { AvatarModule } from "primeng/avatar";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { DialogComponent } from "../../../../@core/components/ui/dialog/dialog.component";
import { SelectModule } from "primeng/select";
import { MultiSelectModule } from "primeng/multiselect";
import { SkeletonModule } from "primeng/skeleton";
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from "../../../../@core/components/ui/button/button.component";
import { DeleteConfirmationComponent } from "../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";
import { PopoverComponent } from "../../../../@core/components/ui/popover/popover.component";
import { SelectComponent, SelectOption, SelectConfig } from "../../../../@core/components/ui/select";
import { MultiSelectComponent, MultiSelectConfig } from "../../../../@core/components/ui/multi-select";
import { DatepickerComponent, DatePickerConfig } from "../../../../@core/components/ui/datepicker";
import { TAG_DATA_TYPES } from "../../../../@core/models/enums";
import { DEACTIVATION_STATUS, IUser } from "../../../../@core/models/model";
import { ToastService } from "../../../../@core/services/toast.service";
import { SearchBarComponent } from "../../../../@shared/components/search-bar/search-bar.component";
import { ExcelUploadDialogComponent, UPLOAD_TYPE } from "../../../../@shared/excel-upload-dialog/excel-upload-dialog.component";
import { FileSelectionComponent } from "../../../../@shared/components/file-selection/file-selection.component";
import { FetchFunction, FileItem, FileSelectionConfig, FileSelectionSubmitEvent } from "../../../../@shared/components/file-selection/file-selection.model";
import { CheckboxComponent } from "../../../../@core/components/ui/checkbox/checkbox.component";
import { UserAvatarPipe } from "../../../../@shared/pipes/user-avatar-pipe";
import { retrieveDisplayNameForUser } from "../../../../common/utils";
import { FilterPipe } from "../../../../pipes/filter.pipe";
import { CheckScreenSize } from "../../../../services/check-screen-size.service";
import { SVG_ICONS } from "../../../constants/constant";
import { SVG } from "../../../../common/svgs";
import { IClient, ICustom, IDocumentType, ILocation, IProject, IRepoExtracted, IScopeOfWork, ITag, ITagType, ITypeOfContract, TAG_FEATURE, TAG_TYPES } from "../../../models/settings.model";
import { TagsService } from "../../../services/tags.service";
import { DocumentTypeService } from "../../../services/document-type.service";
import { ToggleComponent } from "src/app/@core/components/ui/toggle/toggle.component";
import { distinctUntilChanged, debounceTime, Subject, of } from "rxjs";
import { IconSource } from "src/app/@core/components/ui/icon/icon.component";

import { AssetUrlPipe } from "../../../../@shared/pipes/asset-url.pipe";
import { environment } from "src/environments/environment.dev";
import { UserNameDisplayComponent } from "src/app/@shared/components/user-name-display";

@Component({
    selector: 'app-tags',
    imports: [
        CardModule,
        ButtonModule,
        AvatarModule,
        DialogComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SkeletonModule,
        CheckboxModule,
        FileSelectionComponent,
        CheckboxComponent,
        PopoverComponent,
        SelectModule,
        MultiSelectModule,
        ExcelUploadDialogComponent,
        SearchBarComponent,
        TitleCasePipe,
        TooltipModule,
        ButtonComponent,
        SelectComponent,
        MultiSelectComponent,
        DeleteConfirmationComponent,
        ToggleComponent,
        AssetUrlPipe,
        UserNameDisplayComponent
    ],
    standalone: true,
    templateUrl: './tags.component.html',
    styleUrl: './tags.component.scss',
    providers: [FilterPipe]
})
export class TagsComponent implements OnInit {
    @ViewChild('tagTypesContainer') tagTypesContainer!: ElementRef;

    keyPoints: ITagType[] = [];
    repoExtractedTagType: ITagType | undefined;
    selectedTagType: ITagType | null = null;
    TAG_TYPES_ICONS: { [key: string]: string } = {
        [TAG_TYPES.CLIENT]: 'pi pi-user',
        [TAG_TYPES.LOCATION]: 'pi pi-map-marker',
        [TAG_TYPES.SCOPE_OF_WORK]: 'pi pi-bullseye',
        [TAG_TYPES.TYPE_OF_CONTRACT]: 'pi pi-file',
        [TAG_TYPES.CUSTOM]: 'pi pi-tag',
        [TAG_TYPES.PROJECT]: 'pi pi-briefcase',
    };
    TAG_TYPES = TAG_TYPES;
    tags: any[] = []
    tagDialogVisible = false;
    tagAddDialogVisible = false;
    newTagTypeAdded!: string;
    selectedTag: any = null;
    tagTypeLoading: boolean = false
    tagLoading: boolean = true
    searchValue: string = '';
    searchTagTypes: string = '';
    fetchFromRepoVisible = false;
    repoTagsLoading: boolean = false
    repoExtractedTags: (IRepoExtracted & { selected?: boolean })[] = [];

    repoFileSelectionConfig: FileSelectionConfig = {
        title: 'Fetch From Repo',
        submitButtonLabel: 'Fetch Selected',
        showSearch: true,
        searchPlaceholder: 'Search tags',
        selectedPanelTitle: 'Selected Tags',
    };

    fetchRepoTags: FetchFunction = (event) => {
        const query = event.searchQuery?.toLowerCase() || '';
        const filtered = this.repoExtractedTags.filter(tag =>
            !query || tag.name.toLowerCase().includes(query)
        );
        const start = event.page * event.pageSize;
        const items: FileItem[] = filtered.slice(start, start + event.pageSize).map(tag => ({
            id: tag._id ?? tag.name,
            name: tag.name,
            metadata: { originalTag: tag },
        }));
        return of({ items, totalRecords: filtered.length });
    };
    searchText: string = '';
    selectAll: boolean = false;
    icons = SVG_ICONS;
    DEACTIVATION_STATUS = DEACTIVATION_STATUS;
    isEditingTag: boolean = false;
    addTypeLoading: boolean = false;

    searchSubject = new Subject<string>();
    searchTagSubject = new Subject<string>();

    // for infinite scroll   
    currentPage = 1;
    pageSize = 20;
    hasMoreTagTypes = true;
    isFetchingMore = false;

    TAG_DATA_TYPES = TAG_DATA_TYPES;
    TAG_FEATURES = TAG_FEATURE;

    private static featureRequiredValidator(control: AbstractControl): { [key: string]: boolean } | null {
        const val = control.value;
        if (!Array.isArray(val) || val.length === 0) return { required: true };
        return null;
    }

    tagTypeForm: FormGroup = new FormGroup({
        _id: new FormControl(''),
        name: new FormControl('', [Validators.required]),
        description: new FormControl(''),
        tagType: new FormControl(''),
        color: new FormControl(''),
        dataType: new FormControl('', [Validators.required]),
        feature: new FormControl([], [TagsComponent.featureRequiredValidator]),
        documentType: new FormControl(''),
        mandatory: new FormControl(false),
    })

    editTagTypeDialog: boolean = false;
    dataTypeOptions: SelectOption[] = Object.values(TAG_DATA_TYPES).map(type => {
        let icon = '';
        let iconSource: IconSource = 'svg';

        switch (type) {
            case TAG_DATA_TYPES.TEXT:
                icon = SVG.TEXT_TAG_DATA_TYPE;
                break;
            case TAG_DATA_TYPES.DATE:
                icon = SVG.DATE_TAG_DATA_TYPE;
                break;
            case TAG_DATA_TYPES.NUMBER:
                icon = SVG.NUMBER_TAG_DATA_TYPE;
                break;
            case TAG_DATA_TYPES.DROPDOWN:
                icon = SVG.DROPDOWN_FIELDS_TAG_DATA_TYPE;
                break;
        }

        return {
            label: type,
            value: type,
            icon,
            iconSource,
            iconSize: 'default'
        };
    });
    tagTypeOptions: SelectOption[] = Object.values(TAG_TYPES).map(type => ({ label: type, value: type }));
    tagTypeConfig: SelectConfig = {
        placeholder: 'Select Tag Type',
        searchable: false,
        clearable: false,
        appendTo: 'body'
    };
    tagFeatureOptions: SelectOption[] = Object.values(TAG_FEATURE).map(type => ({ label: type, value: type }));
    dataTypeConfig: SelectConfig = {
        placeholder: 'Select Data Type',
        searchable: false,
        clearable: false,
        appendTo: 'body'
    };

    get dynamicDataTypeConfig(): SelectConfig {
        return {
            ...this.dataTypeConfig,
            disabled: this.editTagTypeDialog
        };
    }
    tagFeatureConfig: MultiSelectConfig = {
        placeholder: 'Select project/document',
        searchable: true,
        showToggleAll: true,
        appendTo: 'body'
    };
    isSmallScreen: boolean = false;
    uploadExcelDialogVisible = false;
    uploadTagsExcelDialogVisible = false;
    filteredTagTypes: ITagType[] = [];
    private filterPipe = new FilterPipe()
    documentTypeOptions: SelectOption[] = [];
    documentTypeConfig: SelectConfig = {
        placeholder: 'Select Document Type',
        searchable: false,
        clearable: false,
        appendTo: 'body'
    };

    allowedTagTypesToShowAddButton = [
        TAG_TYPES.CLIENT,
        TAG_TYPES.LOCATION,
        TAG_TYPES.TYPE_OF_CONTRACT,
        TAG_TYPES.SCOPE_OF_WORK,
        TAG_TYPES.PROJECT,
        TAG_TYPES.CUSTOM
    ];

    blockedDataTypesToShowAddButton = [
        TAG_DATA_TYPES.TEXT,
        TAG_DATA_TYPES.NUMBER,
        TAG_DATA_TYPES.DATE
    ];


    // Config for tag value inputs
    tagSelectConfig: SelectConfig = {
        placeholder: 'Select an option',
        searchable: false,
        clearable: true,
        appendTo: 'body'
    };

    tagMultiSelectConfig: MultiSelectConfig = {
        placeholder: 'Select options',
        searchable: false,
        showToggleAll: false,
        appendTo: 'body'
    };

    tagDatepickerConfig: DatePickerConfig = {
        placeholder: 'Select date',
        dateFormat: 'yy-mm-dd',
        showIcon: true,
        appendTo: 'body'
    };

    deleteTagDialogVisible: boolean = false;
    deleteTagTypeDialogVisible: boolean = false;
    selectedTagToDelete: any = null;
    selectedTagTypeToDelete: ITagType | null = null;
    editConfirmationDialogForEditingTagsOfExistingProject: boolean = false;
    ENV = environment;


    @ViewChild('clientTemplate', { static: true }) clientTemplate!: TemplateRef<ElementRef>;
    @ViewChild('locationTemplate', { static: true }) locationTemplate!: TemplateRef<ElementRef>;
    @ViewChild('typeOfContractTemplate', { static: true }) typeOfContractTemplate!: TemplateRef<ElementRef>;
    @ViewChild('scopeOfWorkTemplate', { static: true }) scopeOfWorkTemplate!: TemplateRef<ElementRef>;
    @ViewChild('projectTemplate', { static: true }) projectTemplate!: TemplateRef<ElementRef>;
    @ViewChild('customTemplate', { static: true }) customTemplate!: TemplateRef<ElementRef>;


    templateMap: { [key: string]: TemplateRef<any> } = {};


    constructor(
        private tagService: TagsService,
        private toastService: ToastService,
        private checkScreenSizeService: CheckScreenSize,
        private documentTypeService: DocumentTypeService
    ) { }

    ngOnInit() {
        this.isSmallScreen = this.checkScreenSizeService.checkScreenSize()
        this.templateMap[this.TAG_TYPES.CLIENT] = this.clientTemplate;
        this.templateMap[this.TAG_TYPES.LOCATION] = this.locationTemplate;
        this.templateMap[this.TAG_TYPES.TYPE_OF_CONTRACT] = this.typeOfContractTemplate;
        this.templateMap[this.TAG_TYPES.SCOPE_OF_WORK] = this.scopeOfWorkTemplate;
        this.templateMap[this.TAG_TYPES.PROJECT] = this.projectTemplate;
        this.templateMap[this.TAG_TYPES.CUSTOM] = this.customTemplate;
        this.getTagTypes();
        this.loadDocumentTypes();
        this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(searchValue => {
            this.getTagTypes(this.selectedTagType?._id, searchValue);
        });
        this.searchTagSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(searchValue => {
            this.getTags(searchValue);
        });
    }

    get tagTypeFormControls() {
        return this.tagTypeForm.controls;
    }

    get isDocumentFeatureSelected(): boolean {
        const selectedFeatures = this.tagTypeFormControls['feature'].value as TAG_FEATURE[];
        return selectedFeatures && selectedFeatures.includes(TAG_FEATURE.DOCUMENT);
    }

    loadDocumentTypes() {
        this.documentTypeService.getDocTypes().subscribe((res: any) => {
            if (res.success && res.data) {
                const documentTypes = res.data as IDocumentType[];
                this.documentTypeOptions = documentTypes.map(docType => ({
                    label: docType.name,
                    value: docType._id || ''
                }));
            }
        });
    }

    getTagTypes(selectTagTypeId?: string, searchValue?: string, isLoadMore: boolean = false) {
        if (this.isFetchingMore && isLoadMore) return;
        if (!this.hasMoreTagTypes && isLoadMore) return;

        this.isFetchingMore = true;

        if (!isLoadMore) {
            this.tagTypeLoading = true;
            this.currentPage = 1;
            this.hasMoreTagTypes = true;
        }

        const body: { filters?: { searchValue?: string, page?: number, limit?: number }, getLinkedTags?: boolean } = {
            getLinkedTags: false,
            filters: {
                searchValue: searchValue?.trim() || '',
                page: this.currentPage,
                limit: this.pageSize
            }
        };

        this.tagService.getTagTypes(body).subscribe(res => {
            this.isFetchingMore = false;
            this.tagTypeLoading = false;

            if (!res || !res.success) return;

            const incomingData = res.data.tagTypes || [];

            if (isLoadMore) {
                this.keyPoints = [...this.keyPoints, ...incomingData];
            } else {
                this.keyPoints = incomingData;
            }

            this.filteredTagTypes = [...this.keyPoints];
            this.hasMoreTagTypes = this.keyPoints.length < (res.data.total ?? 0);

            this.repoExtractedTagType = this.keyPoints.find(item => item.tagType == TAG_TYPES.REPO_EXTRACTED);

            if (!isLoadMore && this.keyPoints.length > 0) {
                const selectedTagType = this.keyPoints.find(
                    item => item._id == selectTagTypeId
                );

                if (selectedTagType) {
                    this.selectKeyPoint(selectedTagType);
                } else {
                    this.selectKeyPoint(this.keyPoints[0]);
                }
            }
        });
    }

    onTagTypesScroll() {
        const el = this.tagTypesContainer?.nativeElement;
        if (!el) return;

        const nearBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 100;

        if (nearBottom && this.hasMoreTagTypes && !this.isFetchingMore) {
            this.currentPage++;
            this.getTagTypes(this.selectedTagType?._id, this.searchTagTypes, true);
        }
    }


    selectKeyPoint(keyPoint: ITagType) {
        if (this.selectedTagType == keyPoint) return
        this.selectedTagType = keyPoint;
        this.searchValue = '';
        this.getTags();
    }

    getTags(searchValue?: string) {
        this.tagLoading = true
        const filters: { tag?: ITagType['_id'], searchValue?: string } = {
            tag: this.selectedTagType?._id
        };

        if (searchValue !== undefined && searchValue !== null && searchValue.trim() !== '') {
            filters.searchValue = searchValue.trim();
        }

        this.tagService.getTags(filters).subscribe((res: any) => {
            if (res && res.success) {
                this.tagLoading = false
                this.tags = (res.data || []).map((curr: any) => ({ ...curr, createdByLabel: retrieveDisplayNameForUser(curr.createdBy), createdByInitials: curr.createdBy?.firstName?.charAt(0) + curr.createdBy?.lastName?.charAt(0) }));
                this.filterTags = this.tags;
                if (this.newTagTypeAdded) {
                    this.scrollToTagType(this.newTagTypeAdded);
                    this.newTagTypeAdded = ''
                }
            }
            else
                this.tagLoading = false
        })
    }

    scrollToTagType(tagTypeId: string) {
        setTimeout(() => {
            const element = document.getElementById(`tag-type-${tagTypeId}`);
            if (element && this.tagTypesContainer) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }, 100)
    }

    filterTags: (ITag & ICustom & IClient & IRepoExtracted & IProject & ILocation & {
        createdByLabel: string,
        createdBy: IUser,
        address: string,
    })[] = []

    onFilter() {
        this.searchTagSubject.next(this.searchValue);
    }


    onTagTypesSearchChange() {
        this.currentPage = 1;
        this.hasMoreTagTypes = true;
        this.searchSubject.next(this.searchTagTypes);
    }

    getRandomColor(): string {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    }

    addTag() {
        this.tagDialogVisible = !this.tagDialogVisible;
        if (this.selectedTagType?.tagType === TAG_TYPES.CLIENT) {
            this.selectedTag = {
                name: '',
                address: '',
                licenseNumber: '',
                fax: '',
                website: '',
            } as IClient;
        } else if (this.selectedTagType?.tagType === TAG_TYPES.LOCATION) {
            this.selectedTag = {
                name: '',
                address: '',
                city: '',
                state: '',
                zip: '',
                country: '',
            } as ILocation;
        } else if (this.selectedTagType?.tagType === TAG_TYPES.TYPE_OF_CONTRACT) {
            this.selectedTag = {
                name: '',
                description: '',
            } as ITypeOfContract;
        } else if (this.selectedTagType?.tagType === TAG_TYPES.SCOPE_OF_WORK) {
            this.selectedTag = {
                name: '',
                description: '',
            } as IScopeOfWork;
        }
        else if (this.selectedTagType?.tagType === TAG_TYPES.PROJECT) {
            this.selectedTag = {
                id: '',
                name: '',
                description: '',
            };
        }
        else if (this.selectedTagType?.tagType === TAG_TYPES.CUSTOM) {
            this.selectedTag = {
                value: '',
            };
        }
    }

    editTag(tag: any) {
        this.selectedTag = structuredClone(tag);
        this.tagDialogVisible = true;
        this.isEditingTag = true;
    }

    deleteTag(tag: any) {
        this.selectedTagToDelete = tag;
        this.deleteTagDialogVisible = true;
    }

    confirmDeleteTag() {
        this.tagService.deleteTag(this.selectedTagToDelete._id).subscribe((res: any) => {
            if (res && res.success) {
                this.toastService.success(res.message);
                this.getTags();
                this.deleteTagDialogVisible = false;
            }
        });
    }

    get selectedTemplate() {
        if (this.selectedTagType) {
            return this.templateMap[this.selectedTagType.tagType];
        }
        return null
    }

    saveTag() {
        let valid = true;
        switch (this.selectedTagType?.tagType) {
            case TAG_TYPES.CLIENT:
                valid = this.selectedTag?.name.trim();
                break;
            case TAG_TYPES.LOCATION:
                valid = this.selectedTag?.name.trim();
                break;
            case TAG_TYPES.TYPE_OF_CONTRACT:
                valid = this.selectedTag?.name.trim();
                break;
            case TAG_TYPES.SCOPE_OF_WORK:
                valid = this.selectedTag?.name.trim();
                break;
            case TAG_TYPES.PROJECT:
                valid = this.selectedTag?.id.trim();
                break;
            case TAG_TYPES.CUSTOM:
                valid = this.selectedTag?.name.trim();
                break;
            default:
                valid = false;
        }
        if (valid) {
            this.tagService.saveTag({ ...this.selectedTag, tagType: this.selectedTagType?._id, type: this.selectedTagType?.tagType }).subscribe((res: any) => {
                if (res && res.success) {
                    this.selectedTag = null;
                    this.getTags();
                }
                this.tagDialogVisible = false;
            })
        }
    }

    getSkeletonCount(count: number) {
        return Array(count).fill(0);
    }

    handleClickAddTag() {
        this.tagTypeForm.reset()
        this.editTagTypeDialog = false
        this.tagAddDialogVisible = true
    }

    addTagType(confirmedEdit: boolean = false) {
        this.addTypeLoading = true;
        if (this.tagTypeForm.invalid) {
            this.addTypeLoading = false;
            this.toastService.warn('Please fill all the required fields');
            return
        }

        if (confirmedEdit) {
            this.tagService.saveTagType(this.tagTypeForm.value, confirmedEdit).subscribe((res: any) => {
                if (res && res.success) {
                    this.toastService.success('Tag Type Updated Successfully');
                    this.addTypeLoading = false;
                    this.tagAddDialogVisible = false;
                    this.newTagTypeAdded = res.data._id;
                    this.getTagTypes(res.data._id);
                    this.tagTypeForm.reset();
                    return;
                }
            })
        }

        else {

            const tagTypeData: any = {
                name: this.tagTypeFormControls["name"].value as string,
                description: this.tagTypeFormControls["description"].value as string,
                tagType: this.tagTypeFormControls["tagType"].value as TAG_TYPES ? this.tagTypeFormControls["tagType"].value as TAG_TYPES : TAG_TYPES.CUSTOM,
                color: this.tagTypeFormControls["color"].value as string ? this.tagTypeFormControls["color"].value as string : this.getRandomColor(),
                dataType: this.tagTypeFormControls["dataType"].value ? this.tagTypeFormControls["dataType"].value : TAG_DATA_TYPES.DROPDOWN,
                feature: this.tagTypeFormControls["feature"].value || [],
                mandatory: this.tagTypeFormControls["mandatory"].value || false
            };

            const idValue = this.tagTypeFormControls["_id"].value as string;
            if (idValue) {
                tagTypeData._id = idValue;
            }

            if (this.isDocumentFeatureSelected && this.tagTypeFormControls["documentType"].value) {
                tagTypeData.documentType = this.tagTypeFormControls["documentType"].value;
            }

            this.tagService.saveTagType(tagTypeData).subscribe((res: any) => {
                if (res && res.success) {
                    if (res.data.feature?.includes('Project') && this.editTagTypeDialog === true) {
                        this.editConfirmationDialogForEditingTagsOfExistingProject = true;
                        this.addTypeLoading = false;
                        this.tagAddDialogVisible = false;
                    }
                    else {
                        this.toastService.success(`Tag Type ${this.tagTypeFormControls["_id"].value ? 'Updated' : 'Added'} Successfully`);
                        this.addTypeLoading = false;
                        this.tagAddDialogVisible = false;
                        this.newTagTypeAdded = res.data._id;
                        this.getTagTypes(res.data._id);
                        this.tagTypeForm.reset();
                        return;
                    }
                }
                this.tagAddDialogVisible = false;
            })
            return;
        }
    }

    editTagsOfExistingProject() {
        this.editConfirmationDialogForEditingTagsOfExistingProject = false;
        this.addTagType(true);
    }

    fetchFromRepo() {
        this.repoTagsLoading = true;
        this.repoFileSelectionConfig = {
            ...this.repoFileSelectionConfig,
            title: 'Fetch ' + this.selectedTagType?.name,
        };
        this.tagService.getTags({ tag: this.repoExtractedTagType?._id }).subscribe(res => {
            if (res && res.success) {
                const filterTagNames = new Set(this.filterTags.map(tag => tag.name));
                this.repoExtractedTags = (res.data || []).filter((item: IRepoExtracted) => item.targetTagType == this.selectedTagType?._id)
                    .filter((item: IRepoExtracted) => !filterTagNames.has(item.name));
                this.repoTagsLoading = false;
            } else {
                this.repoTagsLoading = false;
            }
        });
        this.fetchFromRepoVisible = true;
    }

    submitTagsSelection(event: FileSelectionSubmitEvent) {
        const selectedRepoExtracted = (event.allSelectedFiles ?? [])
            .map(f => f.metadata?.originalTag as IRepoExtracted)
            .filter(Boolean);
        this.tagService.addTagsFromRepo({ tags: selectedRepoExtracted, type: this.selectedTagType?.tagType ?? '', tagType: this.selectedTagType?._id ?? '' }).subscribe(res => {
            this.tags = (res.data || []).map((curr: any) => ({ ...curr, createdBy: curr.createdBy, createdByLabel: retrieveDisplayNameForUser(curr.createdBy) }));
            this.filterTags = this.tags;
            this.searchValue = '';
        });
        this.fetchFromRepoVisible = false;
    }

    editTagType(tagType: ITagType) {
        this.editTagTypeDialog = true;
        this.tagTypeForm.patchValue({
            _id: tagType._id,
            name: tagType.name,
            description: tagType.description,
            tagType: tagType.tagType,
            color: tagType.color,
            dataType: tagType.dataType,
            feature: tagType.feature || [],
            documentType: (tagType as any).documentType || '',
            mandatory: tagType.mandatory || false,
        })
        this.tagAddDialogVisible = true;
    }

    deleteTagType(tagType: ITagType) {
        this.selectedTagTypeToDelete = tagType;
        this.deleteTagTypeDialogVisible = true;
    }

    confirmDeleteTagType() {
        this.tagService.deleteTagType(this.selectedTagTypeToDelete!._id).subscribe((res: any) => {
            if (res && res.success) {
                this.toastService.success('Tag Type Deleted Successfully');
                this.getTagTypes();
                this.deleteTagTypeDialogVisible = false;
            }
            this.deleteTagTypeDialogVisible = false;
        });
    }

    handleTagMenuClick(event: any, option: string, tag: ITagType) {
        if (option === 'Edit') {
            this.editTagType(tag)
        } else if (option === 'Delete') {
            this.deleteTagType(tag)
        }
    }

    getTagTypeIcon(tagType: TAG_TYPES, dataType: TAG_DATA_TYPES): string {
        const isCustomType = !(tagType in this.TAG_TYPES_ICONS) || tagType === TAG_TYPES.CUSTOM;

        if (isCustomType) {
            switch (dataType) {
                case TAG_DATA_TYPES.DATE:
                    return 'pi pi-calendar';
                case TAG_DATA_TYPES.TEXT:
                    return 'pi pi-language';
                case TAG_DATA_TYPES.NUMBER:
                    return 'pi pi-calculator';
                case TAG_DATA_TYPES.DROPDOWN:
                    return 'pi pi-tags';
                default:
                    return 'pi pi-question';
            }
        }
        return this.TAG_TYPES_ICONS[tagType];
    }

    downloadSampleExcel() {
        this.tagService.downloadSampleExcelForTags().subscribe(res => {
            if (res.success && res.data) {
                const blob = new Blob([new Uint8Array(res.data.data)], { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
                saveAs(blob, 'Tags Sample Excel.xlsx');
            }
        })
    }

    openUploadExcelDialog() {
        this.uploadExcelDialogVisible = true
    }

    onExcelDialogClose() {
        this.uploadExcelDialogVisible = false
    }

    uploadExcel(event: {
        file: File,
        type: UPLOAD_TYPE
    }) {
        const formData = new FormData()
        formData.append('file', event.file)
        formData.append('uploadType', event.type)
        this.tagService.uploadExcelForTagTypes(formData).subscribe(res => {
            if (res.success) {
                this.getTagTypes()
                if (res.data.buffer) {
                    const blob = new Blob([new Uint8Array(res.data.buffer.data)], { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
                    saveAs(blob, 'Tags Errors Excel.xlsx');
                }
                else {
                    this.toastService.success("Excel uploaded successfully")
                }
            }
        })
    }

    openUploadTagsExcelDialog() {
        this.uploadTagsExcelDialogVisible = true;
    }

    onTagsExcelDialogClose() {
        this.uploadTagsExcelDialogVisible = false;
    }

    downloadTagsSampleExcel() {
        this.tagService.downloadSampleExcelForTagValues().subscribe(res => {
            if (res.success && res.data) {
                const blob = new Blob([new Uint8Array(res.data.data)], { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
                saveAs(blob, 'Tags Values Sample Excel.xlsx');
            }
        })
    }

    uploadTagsExcel(event: {
        file: File,
        type: UPLOAD_TYPE
    }) {
        if (!this.selectedTagType?._id) {
            this.toastService.warn('Please select a tag type first');
            return;
        }

        const formData = new FormData();
        formData.append('file', event.file);
        formData.append('tagTypeId', this.selectedTagType._id);

        this.tagService.uploadExcelForTagValues(formData).subscribe(res => {
            if (res.success) {
                this.getTags();
                if (res.data.buffer) {
                    const blob = new Blob([new Uint8Array(res.data.buffer.data)], { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
                    saveAs(blob, 'Tags Upload Errors.xlsx');
                } else {
                    this.toastService.success("Tags uploaded successfully");
                }
                this.uploadTagsExcelDialogVisible = false;
            }
        })
    }

}
